import React, { useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Grid, Card, Divider, useTheme, Box } from "@mui/material";
import LoadingPage from "./LoadingPage";
import ErrorPage from "./ErrorPage";
import BreadCrumb from "../Components/BreadCrumb";
import RecipeHeader from "../Components/RecipeView/RecipeHeader";
import RecipeImageRibbon from "../Components/RecipeView/RecipeImageRibbon";
import RecipeActions from "../Components/RecipeView/RecipeActions";
import RecipeStats from "../Components/RecipeView/RecipeStats";
import CookingTimes from "../Components/RecipeView/CookingTimes";
import RecipeDescription from "../Components/RecipeView/RecipeDescription";
import NutritionInformation from "../Components/DashboardComponent/NutritionInformation";
import ShareDialog from "../Components/Dialogs/ShareDialog";
import { useUpdateRecipeCounter } from "../Hooks/useUpdateRecipeCounter";
import TaskList from "../Components/ActionManagerComponents/TaskList";
import ReviewForm from "../Components/Forms/ReviewForm";
import ReviewList from "../Components/DashboardComponent/ReviewList";
import { getRecipeImage } from "../constants";

const GET_RECIPE_QUERY = gql`
  query GetRecipe($id: ID!) {
    recipe(id: $id)
  }
`;

const DOWNLOAD_RECIPE_MUTATION = gql`
  mutation DownloadRecipe($recipe_id: ID!) {
    downloadRecipe(recipe_id: $recipe_id) {
      message
      success
      id
    }
  }
`;

const RecipeContent = ({ recipeId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_RECIPE_QUERY, {
    variables: { id: recipeId },
    skip: !recipeId,
    fetchPolicy: "network-only",
  });

  const { incrementRecipeCounter } = useUpdateRecipeCounter();
  const [downloadRecipe] = useMutation(DOWNLOAD_RECIPE_MUTATION, {
    onCompleted: handleDownloadComplete,
    onError: handleDownloadError,
  });

  let recipeData = null;
  if (data?.recipe) {
    recipeData =
      typeof data.recipe === "string" ? JSON.parse(data.recipe) : data.recipe;
  }

  console.log(recipeData);

  const {
    recipe,
    nutrition,
    isLiked,
    isCooked,
    isDownloaded,
    authorName,
    viewsCount = 0,
    likedCount = 0,
    downloadedCount = 0,
    cookedCount = 0,
    ratingCount = 0,
    averageRating = 0,
    allRatings = [],
    isAdmin = false,
  } = recipeData || {};

  function handleDownloadComplete(data) {
    if (data?.downloadRecipe?.success) {
      incrementRecipeCounter(recipeId, "downloaded");
      setTimeout(() => {
        setIsSubmitting(false);
        navigate(`/edit-recipe/${data.downloadRecipe.id}?msg=Download`);
      }, 3000);
    } else {
      setDownloadError(data?.errors?.message || "Download failed");
    }
  }

  function handleDownloadError(error) {
    setIsSubmitting(false);
    setDownloadError(error.message || "Download failed");
  }

  const handleDownload = () => {
    if (isDownloaded || isSubmitting) return;
    setIsSubmitting(true);
    downloadRecipe({ variables: { recipe_id: recipeId } });
  };

  const handleLike = () => {
    if (!isLiked) {
      incrementRecipeCounter(recipeId, "likes").then(refetch);
    }
  };

  const ratingArray =
    typeof allRatings === "string" ? JSON.parse(allRatings) : allRatings || [];

  const ingredients = React.useMemo(() => {
    if (!recipe?.recipe_tasks) return [];
    try {
      return typeof recipe.recipe_tasks === "string"
        ? JSON.parse(recipe.recipe_tasks)
        : recipe.recipe_tasks;
    } catch (error) {
      console.error("Error parsing ingredients:", error);
      return [];
    }
  }, [recipe]);

  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage message={error.message} />;
  if (!recipe) return <ErrorPage message="Recipe not found" />;

  return (
    <>
      <BreadCrumb
        links={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Recipes", href: "/recipes" },
        ]}
        currentPage={recipe.recipe_name}
      />

      <Card
        sx={{
          p: 3,
          boxShadow: 3,
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Grid container spacing={4}>
          {/* Main Content Column - Mobile Order */}
          <Grid item xs={12} md={8}>
            <RecipeHeader
              recipe={recipe}
              authorName={authorName}
              ratingCount={ratingCount}
              averageRating={averageRating}
            />

            <RecipeImageRibbon
              recipe={recipe}
              recipe_image={recipe?.recipe_image}
            />

            <RecipeStats
              viewsCount={viewsCount}
              likedCount={likedCount}
              downloadedCount={downloadedCount}
              cookedCount={cookedCount}
              handleLike={handleLike}
              handleShare={() => setShareOpen(true)}
              isLiked={isLiked}
            />
            <Box height={20} />
            <CookingTimes
              prepTime={recipe.prep_time}
              cookTime={recipe.cook_time}
              serving={recipe.serving}
            />

            {/* Mobile-only components */}
            <Box sx={{ display: { xs: "block", md: "none" }, width: "100%" }}>
              <RecipeActions
                isAdmin={isAdmin}
                recipeId={recipeId}
                handleCook={() => navigate(`/cook-recipe/${recipeId}`)}
                handleDownload={handleDownload}
                isCooked={isCooked}
                isDownloaded={isDownloaded}
                isSubmitting={isSubmitting}
                downloadError={downloadError}
              />
              <Box height={20} />
              <TaskList
                tasks={ingredients}
                isAuthor={isAdmin}
                fromViewPage={true}
              />
              <Box height={20} />
              <NutritionInformation nutrition={nutrition} />
              <Box height={20} />
            </Box>

            <RecipeDescription description={recipe.recipe_description} />

            <ReviewList ratingArray={ratingArray} theme={theme} />
            <ReviewForm refetchParentData={refetch} recipe_id={recipe?.id} />
          </Grid>

          {/* Right Column - Desktop Only */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: { xs: "none", md: "block" },
              position: "sticky",
              top: theme.spacing(8),
              height: "fit-content",
            }}
          >
            <RecipeActions
              isAdmin={isAdmin}
              recipeId={recipeId}
              handleCook={() => navigate(`/cook-recipe/${recipeId}`)}
              handleDownload={handleDownload}
              isCooked={isCooked}
              isDownloaded={isDownloaded}
              isSubmitting={isSubmitting}
              downloadError={downloadError}
            />

            <TaskList
              tasks={ingredients}
              isAuthor={isAdmin}
              fromViewPage={true}
            />

            <Divider sx={{ my: 3 }} />

            <NutritionInformation nutrition={nutrition} />
          </Grid>
        </Grid>
      </Card>

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        recipe={recipe}
        image={getRecipeImage(recipe?.recipe_image)}
      />
    </>
  );
};

export default function RecipeViewPage() {
  const { recipe_id } = useParams();
  return <RecipeContent recipeId={parseInt(recipe_id)} />;
}

RecipeContent.propTypes = {
  recipeId: PropTypes.number.isRequired,
};
